import React, { useState, useCallback } from 'react';
import {
  Table,
  Button,
  Space,
  Popconfirm,
  Typography,
  Card,
  Modal,
  Form,
  Input,
  Select,
  Avatar,
  message,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  UserOutlined,
  PictureOutlined,
  RobotOutlined,
  HistoryOutlined,
} from '@ant-design/icons';
import CharacterCheckpointsDrawer from './CharacterCheckpointsDrawer';
import { SourceCharacter } from '../../types';
import { sourceStoriesApi, CreateSourceCharacterRequest, UpdateSourceCharacterRequest } from '../../services/api';
import { useApi, useAsyncOperation } from '../../hooks';
import { formatDate } from '../../utils';

const { Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;

interface CharactersTabProps {
  storyId: string;
}

const CharactersTab: React.FC<CharactersTabProps> = ({ storyId }) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isCheckpointsVisible, setIsCheckpointsVisible] = useState(false);
  const [isSuggestingName, setIsSuggestingName] = useState(false);
  const [editingCharacter, setEditingCharacter] = useState<SourceCharacter | null>(null);
  const [selectedCharacter, setSelectedCharacter] = useState<SourceCharacter | null>(null);
  const [form] = Form.useForm();

  const { loading: asyncLoading, execute: executeAsync } = useAsyncOperation();

  // Fetch characters
  const {
    data: charactersData,
    loading: charactersLoading,
    refresh: refreshCharacters,
  } = useApi(
    useCallback(() => sourceStoriesApi.getSourceCharacters(storyId), [storyId]),
    { immediate: true }
  );

  const handleCreate = () => {
    setEditingCharacter(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEdit = (character: SourceCharacter) => {
    setEditingCharacter(character);
    form.setFieldsValue({
      ...character,
      localizedName: character.localizedData?.name
    });
    setIsModalVisible(true);
  };

  const handleDelete = async (character: SourceCharacter) => {
    await executeAsync(
      () => sourceStoriesApi.deleteSourceCharacter(storyId, character.id),
      { successMessage: 'Character deleted successfully!' }
    );
    refreshCharacters();
  };

  const handleManageCheckpoints = (character: SourceCharacter) => {
    setSelectedCharacter(character);
    setIsCheckpointsVisible(true);
  };

  const handleSuggestName = async () => {
    const values = form.getFieldsValue();
    if (!values.name) {
      message.warning('Please enter character original name first');
      return;
    }
    
    setIsSuggestingName(true);
    try {
      const characterId = editingCharacter?.id || 'new';
      const result = await sourceStoriesApi.suggestCharacterName(storyId, characterId, {
        name: values.name,
        description: values.description
      });
      if (result.data.suggestedName) {
        form.setFieldsValue({ localizedName: result.data.suggestedName });
        message.success(`AI suggested: ${result.data.suggestedName}`);
      }
    } catch (error) {
      console.error('Failed to suggest name:', error);
      message.error('Failed to suggest name with AI');
    } finally {
      setIsSuggestingName(false);
    }
  };

  const handleSubmit = async (values: any) => {
    const { localizedName, ...rest } = values;
    const finalValues = {
      ...rest,
      localizedData: {
        ...(editingCharacter?.localizedData || {}),
        name: localizedName || ''
      }
    };

    if (editingCharacter) {
      await executeAsync(
        () => sourceStoriesApi.updateSourceCharacter(storyId, editingCharacter.id, finalValues),
        { successMessage: 'Character updated successfully!' }
      );
    } else {
      await executeAsync(
        () => sourceStoriesApi.createSourceCharacter(storyId, finalValues as CreateSourceCharacterRequest),
        { successMessage: 'Character created successfully!' }
      );
    }
    setIsModalVisible(false);
    refreshCharacters();
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'PROTAGONIST':
        return 'gold';
      case 'ANTAGONIST':
        return 'volcano';
      case 'SUPPORTING':
        return 'blue';
      case 'BACKGROUND':
        return 'gray';
      default:
        return undefined;
    }
  };

  const columns = [
    {
      title: 'Character',
      key: 'character',
      render: (_: any, record: SourceCharacter) => (
        <Space>
          <Avatar 
            src={record.avatarUrl ? <img src={record.avatarUrl} alt={record.name} style={{ objectPosition: 'top center' }} /> : undefined}
            icon={<UserOutlined />} 
            style={{ backgroundColor: getRoleColor(record.role || '') }}
          />
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <Space>
              <Typography.Text strong>{record.name}</Typography.Text>
              {record.localizedData?.name && (
                <Typography.Text type="success" style={{ fontSize: '12px' }}>
                  ({record.localizedData.name})
                </Typography.Text>
              )}
            </Space>
            <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
              {record.role}
            </Typography.Text>
          </div>
        </Space>
      ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: 'Created Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => formatDate(date),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 160,
      render: (_: any, record: SourceCharacter) => (
        <Space>
          <Button
            type="text"
            icon={<HistoryOutlined />}
            onClick={() => handleManageCheckpoints(record)}
            title="Checkpoints & Visual Identity"
          />
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            title="Edit"
          />
          <Popconfirm
            title="Are you sure you want to delete this character?"
            onConfirm={() => handleDelete(record)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              title="Delete"
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>
          Character Management
        </Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleCreate}
        >
          Add Character
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={charactersData?.data || []}
        rowKey="id"
        loading={charactersLoading}
      />

      <Modal
        title={editingCharacter ? 'Edit Character' : 'Add New Character'}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="name"
            label="Character Name"
            rules={[{ required: true, message: 'Please enter character name' }]}
          >
            <Input placeholder="Enter name" />
          </Form.Item>

          <Form.Item
            name="localizedName"
            label="Localized Name"
          >
            <Input 
              placeholder="Enter name for target market" 
              suffix={
                <Button
                  type="text"
                  size="small"
                  icon={<RobotOutlined />}
                  loading={isSuggestingName}
                  onClick={handleSuggestName}
                  title="AI Suggest Name"
                />
              }
            />
          </Form.Item>

          <Form.Item
            name="role"
            label="Role"
            rules={[{ required: true, message: 'Please select role' }]}
          >
            <Select placeholder="Select role">
              <Option value="PROTAGONIST">Protagonist</Option>
              <Option value="ANTAGONIST">Antagonist</Option>
              <Option value="SUPPORTING">Supporting</Option>
              <Option value="BACKGROUND">Background</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="avatarUrl"
            label="Avatar URL"
          >
            <Input placeholder="Enter avatar image URL" />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
          >
            <TextArea rows={4} placeholder="Describe character personality, background, etc." />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setIsModalVisible(false)}>
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={asyncLoading}
              >
                {editingCharacter ? 'Update' : 'Create'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {selectedCharacter && (
        <CharacterCheckpointsDrawer
          visible={isCheckpointsVisible}
          character={selectedCharacter}
          storyId={storyId}
          onClose={() => {
            setIsCheckpointsVisible(false);
            setSelectedCharacter(null);
            refreshCharacters();
          }}
        />
      )}
    </Card>
  );
};

export default CharactersTab;
